// @flowgenix/shared-ui/utils/overlays
//
// Phase 2 / Step E follow-up — portable confirm() + toast() helpers for
// learner-app and coach-app. Both apps mount a single <OverlayHost/>
// near their app root; anywhere in the React tree can then call
//
//   await confirm({ title: 'Delete?', message: 'This is permanent.', danger: true });
//   toast.success('Saved');
//
// without prop-drilling state and without the legacy window.confirm
// blocking the JS thread.
//
// We deliberately don't import the agent_portal ToastWidget here —
// agent_portal is a separate webpack workspace and its widget components
// aren't reachable from the learner-app / coach-app Vite trees. A future
// consolidation pass can collapse the two implementations.

import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

// ── Pub/sub backbone ─────────────────────────────────────────────────

const _subs = new Set();
let _seq = 0;

function _emit(msg) {
  _subs.forEach((fn) => fn(msg));
}

// ── Toast API ────────────────────────────────────────────────────────

function _addToast(opts) {
  const id = ++_seq;
  _emit({ type: 'toast:add', toast: { id, ...opts } });
  return id;
}

export const toast = {
  show: (message, opts = {}) => _addToast({ message, variant: 'info', ...opts }),
  info: (message, opts = {}) => _addToast({ message, variant: 'info', ...opts }),
  success: (message, opts = {}) => _addToast({ message, variant: 'success', ...opts }),
  warn: (message, opts = {}) => _addToast({ message, variant: 'warn', ...opts }),
  error: (message, opts = {}) => _addToast({ message, variant: 'error', ...opts }),
  dismiss: (id) => _emit({ type: 'toast:remove', id }),
};

// ── Confirm API ──────────────────────────────────────────────────────

/**
 * confirm({ title, message, confirmLabel?, cancelLabel?, danger? })
 *   -> Promise<boolean>  (resolves true on confirm, false on cancel/escape)
 *
 * Drop-in replacement for window.confirm() that doesn't block the JS
 * thread and matches the tenant theme. Always wrap the call in await:
 *
 *   const ok = await confirm({
 *     title: 'Delete tutorial?',
 *     message: 'This cannot be undone.',
 *     confirmLabel: 'Delete',
 *     danger: true,
 *   });
 *   if (!ok) return;
 */
export function confirm(opts = {}) {
  return new Promise((resolve) => {
    const id = ++_seq;
    _emit({
      type: 'confirm:open',
      id,
      message: typeof opts === 'string' ? opts : opts.message || '',
      title: opts.title || 'Are you sure?',
      confirmLabel: opts.confirmLabel || 'Confirm',
      cancelLabel: opts.cancelLabel || 'Cancel',
      danger: Boolean(opts.danger),
      askReason: Boolean(opts.askReason),
      reasonLabel: opts.reasonLabel || 'Reason',
      resolve,
    });
  });
}

/**
 * Like confirm(), plus a required free-text reason.
 * Resolves with the trimmed reason, or null when the user cancels.
 *
 *   const reason = await confirmWithReason({ title: 'Publish anyway?', message: '…' });
 *   if (!reason) return;
 */
export function confirmWithReason(opts = {}) {
  return confirm({ ...opts, askReason: true }).then((r) => (typeof r === 'string' && r ? r : null));
}

// ── OverlayHost — render once near the app root ──────────────────────

const VARIANT_STYLES = {
  info: { bg: 'rgba(59, 130, 246, 0.95)', fg: '#ffffff' },
  success: { bg: 'rgba(34, 197, 94, 0.95)', fg: '#ffffff' },
  warn: { bg: 'rgba(234, 179, 8, 0.95)', fg: '#1a1a1a' },
  error: { bg: 'rgba(239, 68, 68, 0.95)', fg: '#ffffff' },
};

function ToastItem({ t, onDismiss }) {
  const variant = VARIANT_STYLES[t.variant] || VARIANT_STYLES.info;
  return (
    <div
      role="alert"
      style={{
        background: variant.bg,
        color: variant.fg,
        padding: '12px 16px',
        borderRadius: 'var(--fg-radius-md, 8px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        minWidth: 240,
        maxWidth: 480,
        fontSize: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        pointerEvents: 'auto',
      }}
    >
      <span>{t.message}</span>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: 'transparent',
          border: 0,
          color: 'inherit',
          cursor: 'pointer',
          fontSize: 18,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}
ToastItem.propTypes = {
  t: PropTypes.shape({
    id: PropTypes.number,
    message: PropTypes.string.isRequired,
    variant: PropTypes.oneOf(['info', 'success', 'warn', 'error']),
  }).isRequired,
  onDismiss: PropTypes.func.isRequired,
};

function ConfirmDialog({ req, onResolve }) {
  const [reason, setReason] = useState('');
  const needsReason = Boolean(req.askReason);
  const reasonMissing = needsReason && !reason.trim();
  const finish = useCallback(
    (ok) => {
      try {
        req.resolve(ok && needsReason ? reason.trim() : ok);
      } catch (_) {
        /* user already navigated */
      }
      onResolve(req.id);
    },
    [req, onResolve, needsReason, reason],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') finish(false);
      else if (e.key === 'Enter' && !needsReason) finish(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [finish, needsReason]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="overlay-confirm-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) finish(false);
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: 'var(--fg-bg-card, #1a1a2e)',
          color: 'var(--fg-text-1, #e0e0e0)',
          borderRadius: 'var(--fg-radius-lg, 12px)',
          width: 'min(420px, 92vw)',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
          overflow: 'hidden',
        }}
      >
        <header
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <h3 id="overlay-confirm-title" style={{ margin: 0, fontSize: 18 }}>
            {req.title}
          </h3>
        </header>
        <div
          style={{
            padding: '16px 20px',
            flex: 1,
            fontSize: 14,
            lineHeight: 1.5,
            whiteSpace: needsReason ? 'pre-wrap' : undefined,
            overflowY: 'auto',
          }}
        >
          {req.message}
          {needsReason && (
            <label style={{ display: 'block', marginTop: 12 }}>
              <span style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                {req.reasonLabel}
              </span>
              <textarea
                aria-label={req.reasonLabel}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                autoFocus
                style={{ width: '100%', boxSizing: 'border-box', fontSize: 14, padding: 8 }}
              />
            </label>
          )}
        </div>
        <footer
          style={{
            padding: '12px 20px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={() => finish(false)}
            style={{
              background: 'transparent',
              color: 'var(--fg-text-1, #e0e0e0)',
              border: '1px solid rgba(255,255,255,0.18)',
              padding: '8px 16px',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            {req.cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => finish(true)}
            disabled={reasonMissing}
            autoFocus={!needsReason}
            style={{
              opacity: reasonMissing ? 0.5 : 1,
              background: req.danger
                ? 'var(--fg-color-danger, #ef4444)'
                : 'var(--fg-color-primary, #4f46e5)',
              color: '#ffffff',
              border: 0,
              padding: '8px 16px',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {req.confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
ConfirmDialog.propTypes = {
  req: PropTypes.object.isRequired,
  onResolve: PropTypes.func.isRequired,
};

export function OverlayHost() {
  const [toasts, setToasts] = useState([]);
  const [confirmQueue, setConfirmQueue] = useState([]);

  useEffect(() => {
    const sub = (msg) => {
      if (msg.type === 'toast:add') {
        setToasts((prev) => [...prev, msg.toast]);
        if (msg.toast.duration !== 0) {
          const ttl = msg.toast.duration ?? 4000;
          setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== msg.toast.id));
          }, ttl);
        }
      } else if (msg.type === 'toast:remove') {
        setToasts((prev) => prev.filter((t) => t.id !== msg.id));
      } else if (msg.type === 'confirm:open') {
        setConfirmQueue((prev) => [...prev, msg]);
      }
    };
    _subs.add(sub);
    return () => _subs.delete(sub);
  }, []);

  const closeConfirm = useCallback((id) => {
    setConfirmQueue((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const top = confirmQueue[0];

  return (
    <>
      <div
        role="region"
        aria-label="Notifications"
        style={{
          position: 'fixed',
          right: 'max(env(safe-area-inset-right, 0px), 16px)',
          bottom: 'max(env(safe-area-inset-bottom, 0px), 16px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          zIndex: 1100,
          pointerEvents: 'none',
        }}
      >
        {toasts.map((t) => (
          <ToastItem
            key={t.id}
            t={t}
            onDismiss={() => _emit({ type: 'toast:remove', id: t.id })}
          />
        ))}
      </div>
      {top && <ConfirmDialog req={top} onResolve={closeConfirm} />}
    </>
  );
}

export default OverlayHost;
